import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, ScrollView } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Search, Check, ChevronRight } from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { getProfessionTypes, getProfessions } from '../../../../services/shared';
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, sortProfessionsGeneralFirst } from '../../../../utils/professionUtils';
import { getParentTypeCode, professionTypeIcons, sharedStyles } from '../../../Jobs/components';

const StepProfession = ({ formData, updateFormData, goToStep }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProfessionTypes, setExpandedProfessionTypes] = useState([]);
  const [expandedSubtypes, setExpandedSubtypes] = useState([]);

  const country = formData.country;

  // Fetch profession types (level 1 + 2 hierarchy) and professions (level 3)
  const { data: typesData, isLoading: typesLoading } = useQuery(
    ['professionTypes'],
    () => getProfessionTypes(),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
  );

  const { data: professionsData, isLoading: professionsLoading } = useQuery(
    ['professions', country],
    () => getProfessions(country || 'MY'),
    { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
  );

  const isLoading = typesLoading || professionsLoading;

  // Organize into a 3-level hierarchy: Type -> Sub-type -> Profession.
  // Empty branches (no professions) are pruned so only selectable paths show.
  const organizedProfessions = useMemo(() => {
    const professionTypes = typesData?.data?.data || [];
    const professions = professionsData?.data?.data || [];

    const hierarchy = {};

    // Parent types: single letter (a, b, ...) or "x" standalone (e-x, f-x)
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (isTopLevel) {
        hierarchy[code] = { ...type, subtypes: [], professions: [] };
      }
    });

    // Sub-types nested under their parent
    professionTypes.forEach(type => {
      const code = type.code;
      const isTopLevel = !code.includes('-') || /^[a-z]-x$/i.test(code);
      if (!isTopLevel) {
        const parentCode = getParentTypeCode(code);
        if (hierarchy[parentCode]) {
          hierarchy[parentCode].subtypes.push({ ...type, professions: [] });
        }
      }
    });

    // Assign professions to their sub-type or directly to the parent type
    professions.forEach(profession => {
      const typeCode = profession.profession_type_code;
      if (!typeCode || typeof typeCode !== 'string') return;

      const parentCode = getParentTypeCode(typeCode);
      if (!hierarchy[parentCode]) return;

      const subType = hierarchy[parentCode].subtypes.find(s => s.code === typeCode);
      if (subType) {
        subType.professions.push(profession);
      } else {
        hierarchy[parentCode].professions.push(profession);
      }
    });

    // Sort each level alphabetically, then pin each list's "general" entry
    // (e.g. General Practitioner within Doctor > GP) above the rest
    Object.values(hierarchy).forEach(type => {
      type.subtypes.sort((a, b) =>
        getLocalizedProfessionTypeName(a, country).localeCompare(getLocalizedProfessionTypeName(b, country))
      );
      type.subtypes.forEach(sub => {
        sub.professions.sort((a, b) =>
          getLocalizedProfessionName(a, country).localeCompare(getLocalizedProfessionName(b, country))
        );
        sub.professions = sortProfessionsGeneralFirst(sub.professions, sub.code, country);
      });
      type.professions.sort((a, b) =>
        getLocalizedProfessionName(a, country).localeCompare(getLocalizedProfessionName(b, country))
      );
      type.professions = sortProfessionsGeneralFirst(type.professions, type.code, country);
    });

    // Prune empty sub-types and empty types
    const pruned = {};
    Object.entries(hierarchy).forEach(([code, type]) => {
      const subtypesWithProfs = type.subtypes.filter(s => s.professions.length > 0);
      if (subtypesWithProfs.length > 0 || type.professions.length > 0) {
        pruned[code] = { ...type, subtypes: subtypesWithProfs };
      }
    });

    return pruned;
  }, [typesData, professionsData, country]);

  // Search across all 3 levels (type, sub-type, profession names)
  const filteredProfessions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return organizedProfessions;

    const filtered = {};

    Object.entries(organizedProfessions).forEach(([code, type]) => {
      const typeMatches = getLocalizedProfessionTypeName(type, country).toLowerCase().includes(query);
      if (typeMatches) {
        filtered[code] = { ...type };
        return;
      }

      const matchingSubtypes = [];
      type.subtypes.forEach(subType => {
        const subTypeMatches = getLocalizedProfessionTypeName(subType, country).toLowerCase().includes(query);
        if (subTypeMatches) {
          matchingSubtypes.push({ ...subType });
        } else {
          const matchingProfs = subType.professions.filter(p =>
            getLocalizedProfessionName(p, country).toLowerCase().includes(query)
          );
          if (matchingProfs.length > 0) {
            matchingSubtypes.push({ ...subType, professions: matchingProfs });
          }
        }
      });

      const matchingDirectProfessions = type.professions.filter(p =>
        getLocalizedProfessionName(p, country).toLowerCase().includes(query)
      );

      if (matchingSubtypes.length > 0 || matchingDirectProfessions.length > 0) {
        filtered[code] = { ...type, subtypes: matchingSubtypes, professions: matchingDirectProfessions };
      }
    });

    return filtered;
  }, [organizedProfessions, searchQuery, country]);

  const selectedProfessionsSet = useMemo(
    () => new Set(formData.profession_ids || []),
    [formData.profession_ids]
  );

  const searchActive = searchQuery.trim().length > 0;

  const toggleProfessionType = (typeCode) => {
    setExpandedProfessionTypes(prev =>
      prev.includes(typeCode) ? prev.filter(c => c !== typeCode) : [...prev, typeCode]
    );
  };

  const toggleSubtype = (subtypeCode) => {
    setExpandedSubtypes(prev =>
      prev.includes(subtypeCode) ? prev.filter(c => c !== subtypeCode) : [...prev, subtypeCode]
    );
  };

  // Select/deselect an actual profession (level 3). Multi-select.
  const handleSelectProfession = (profession) => {
    const currentIds = formData.profession_ids || [];
    const currentProfessions = formData.professions || [];
    const alreadySelected = currentIds.includes(profession.id);

    if (alreadySelected) {
      const newIds = currentIds.filter(id => id !== profession.id);
      const newProfessions = currentProfessions.filter(p => p.id !== profession.id);
      updateFormData('profession_ids', newIds);
      updateFormData('professions', newProfessions);
      updateFormData('profession_id', newIds[0] || null);
    } else {
      updateFormData('profession_ids', [...currentIds, profession.id]);
      updateFormData('professions', [...currentProfessions, profession]);
      updateFormData('profession_id', profession.id);

      // Record the parent profession type for the registration payload (profession_type_code)
      const parentCode = getParentTypeCode(profession.profession_type_code);
      if (parentCode) {
        updateFormData('profession', parentCode);
        const parentType = organizedProfessions[parentCode];
        if (parentType) {
          updateFormData('professionName', getLocalizedProfessionTypeName(parentType, country));
        }
      }
    }
  };

  const renderProfessionLeaf = (profession) => {
    const isSelected = selectedProfessionsSet.has(profession.id);
    const professionName = getLocalizedProfessionName(profession, country);
    if (!professionName || professionName.length === 0 || professionName === '#N/A') return null;

    return (
      <TouchableOpacity
        key={profession.id}
        style={[sharedStyles.professionItem, isSelected && sharedStyles.professionItemSelected]}
        onPress={() => handleSelectProfession(profession)}
      >
        <View style={[sharedStyles.professionCheckbox, isSelected && sharedStyles.professionCheckboxSelected]}>
          {isSelected && <Check size={12} color="#ffffff" />}
        </View>
        <Text style={[sharedStyles.professionItemText, isSelected && sharedStyles.professionItemTextSelected]}>
          {professionName}
        </Text>
      </TouchableOpacity>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={webColors.primary} />
        <Text style={styles.loadingText}>Loading profession types...</Text>
      </View>
    );
  }

  const typeEntries = Object.entries(filteredProfessions);
  const selectedCount = (formData.profession_ids || []).length;

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <View style={styles.stepIcon}>
          <Briefcase size={48} color={webColors.primary} />
        </View>
        <Text style={styles.stepTitle}>Select Profession Type</Text>
        <Text style={styles.stepSubtitle}>Select your medical profession type</Text>
      </View>

      <View style={styles.searchWrapper}>
        <View style={styles.searchIcon}>
          <Search size={16} color={webColors.textMuted} />
        </View>
        <TextInput
          style={styles.searchInput}
          placeholder="Search profession, specialization or type"
          placeholderTextColor={webColors.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      <View style={styles.treeCard}>
        <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={true}>
          {typeEntries.length === 0 ? (
            <Text style={styles.noResultsText}>No professions found</Text>
          ) : (
            typeEntries.map(([typeCode, typeData]) => {
              const isExpanded = expandedProfessionTypes.includes(typeCode) || searchActive;
              const iconConfig = professionTypeIcons[typeCode] || professionTypeIcons.default;
              const hasSubtypes = typeData.subtypes && typeData.subtypes.length > 0;
              const allProfessionsInType = [
                ...typeData.professions,
                ...(typeData.subtypes || []).flatMap(s => s.professions),
              ];
              const selectedInType = allProfessionsInType.filter(p => selectedProfessionsSet.has(p.id)).length;

              return (
                <View key={typeCode} style={sharedStyles.professionTypeGroup}>
                  {/* Level 1: Profession Type */}
                  <TouchableOpacity
                    style={[sharedStyles.professionTypeHeader, isExpanded && sharedStyles.professionTypeHeaderExpanded]}
                    onPress={() => toggleProfessionType(typeCode)}
                  >
                    <Text style={sharedStyles.professionTypeIcon}>{iconConfig.icon}</Text>
                    <Text style={[sharedStyles.professionTypeLabel, isExpanded && sharedStyles.professionTypeLabelExpanded]}>
                      {getLocalizedProfessionTypeName(typeData, country)}
                    </Text>
                    {selectedInType > 0 && (
                      <View style={[sharedStyles.professionTypeCount, { backgroundColor: iconConfig.bgColor }]}>
                        <Text style={[sharedStyles.professionTypeCountText, { color: iconConfig.color }]}>
                          {selectedInType}
                        </Text>
                      </View>
                    )}
                    <ChevronRight
                      size={16}
                      color={isExpanded ? webColors.primary : webColors.textMuted}
                      style={{ transform: [{ rotate: isExpanded ? '90deg' : '0deg' }] }}
                    />
                  </TouchableOpacity>

                  {isExpanded && (
                    <View style={sharedStyles.professionItemsList}>
                      {/* Level 2: Sub-types */}
                      {hasSubtypes && typeData.subtypes.map(subType => {
                        const isSubExpanded = expandedSubtypes.includes(subType.code) || searchActive;
                        const selectedInSub = subType.professions.filter(p => selectedProfessionsSet.has(p.id)).length;

                        return (
                          <View key={subType.code} style={sharedStyles.professionSubtypeGroup}>
                            <TouchableOpacity
                              style={[sharedStyles.professionSubtypeHeader, isSubExpanded && sharedStyles.professionSubtypeHeaderExpanded]}
                              onPress={() => toggleSubtype(subType.code)}
                            >
                              <Text style={[sharedStyles.professionSubtypeLabel, isSubExpanded && sharedStyles.professionSubtypeLabelExpanded]}>
                                {getLocalizedProfessionTypeName(subType, country)}
                              </Text>
                              {selectedInSub > 0 && (
                                <View style={[sharedStyles.professionSubtypeCount, { backgroundColor: iconConfig.bgColor }]}>
                                  <Text style={[sharedStyles.professionSubtypeCountText, { color: iconConfig.color }]}>
                                    {selectedInSub}
                                  </Text>
                                </View>
                              )}
                              <ChevronRight
                                size={14}
                                color={isSubExpanded ? webColors.primary : webColors.textMuted}
                                style={{ transform: [{ rotate: isSubExpanded ? '90deg' : '0deg' }] }}
                              />
                            </TouchableOpacity>

                            {/* Level 3: Professions under sub-type */}
                            {isSubExpanded && (
                              <View style={sharedStyles.professionSubtypeItemsList}>
                                {subType.professions.map(renderProfessionLeaf)}
                              </View>
                            )}
                          </View>
                        );
                      })}

                      {/* Level 3: Direct professions (types without sub-types) */}
                      {typeData.professions.map(renderProfessionLeaf)}
                    </View>
                  )}
                </View>
              );
            })
          )}
        </ScrollView>
      </View>

      <View style={styles.btnRow}>
        <TouchableOpacity style={styles.btnBack} onPress={() => goToStep(1)}>
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, selectedCount === 0 && styles.btnNextDisabled]}
          onPress={() => goToStep(3)}
          disabled={selectedCount === 0}
        >
          <Text style={[styles.btnNextText, selectedCount === 0 && styles.btnNextTextDisabled]}>
            {selectedCount > 0 ? `Next (${selectedCount})` : 'Next'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: webColors.textSecondary,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -9 }],
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 46,
    paddingVertical: 14,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  treeCard: {
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    backgroundColor: webColors.cardBackground,
    marginBottom: 16,
    overflow: 'hidden',
  },
  scrollContainer: {
    maxHeight: 380,
  },
  noResultsText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    paddingVertical: 32,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepProfession;
